import { useEffect, useRef, useState } from 'react';
import { Menu, X, Globe, Check, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { navLinks, LOGO_URL } from '../mock';
import { Button } from './ui/button';
import { useI18n } from '../contexts/I18nContext';
import { languages } from '../translations';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * Hover-first dropdown for the primary nav.
 * - Opens on mouseenter / focus
 * - Closes on mouseleave (with 150ms buffer so users can travel to the menu)
 * - Closes on Escape and on link click within the menu
 * Keyboard accessible: Tab to trigger opens, Tab through items closes on leave.
 */
function HoverDropdown({ triggerCls, triggerLabel, testId, children }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const wrapRef = useRef(null);

  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  // Close when focus leaves the wrapper entirely (keyboard flow)
  const onBlur = (e) => {
    if (!wrapRef.current?.contains(e.relatedTarget)) setOpen(false);
  };

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      onFocus={openNow}
      onBlur={onBlur}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`${triggerCls} inline-flex items-center gap-1`}
        data-testid={testId}
      >
        {triggerLabel}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        <span className="absolute -bottom-0.5 left-0 w-0 h-[2px] bg-[#E5614C] group-hover:w-full transition-[width] duration-300" />
      </button>

      {/* Menu */}
      <div
        role="menu"
        className={`absolute left-0 top-full pt-2 min-w-[200px] transition-[opacity,transform] duration-150 ${
          open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'
        }`}
        onClick={() => setOpen(false)}
      >
        <div className="rounded-xl bg-white border border-[#2A1B1A]/8 shadow-[0_20px_40px_-15px_rgba(42,27,26,0.25)] p-2 min-w-[200px]">
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Collapsible mobile nav group — parent link expands to reveal its children.
 * Auto-expands when the current pathname matches any child href so the user
 * lands with context. Uses a coral accent bar + chevron for on-brand feel.
 */
function MobileNavGroup({ link, t, isHome, closeMobile }) {
  const location = useLocation();
  const isChildActive = link.children.some((c) => !c.isHash && location.pathname === c.href);
  const [open, setOpen] = useState(isChildActive);

  return (
    <div
      className="rounded-2xl bg-[#FAF3EE] border border-[#2A1B1A]/8 overflow-hidden"
      data-testid={`mobile-nav-group-${link.key}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-5 py-4 text-left group"
        data-testid={`mobile-nav-trigger-${link.key}`}
      >
        <span className="text-[#2A1B1A] font-semibold text-[17px] group-hover:text-[#E5614C] transition-colors">
          {t(link.key)}
        </span>
        <span
          className={`h-8 w-8 rounded-full bg-white flex items-center justify-center text-[#E5614C] transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        >
          <ChevronDown className="h-4 w-4" />
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
      >
        <div className="overflow-hidden">
          <div className="relative pl-6 pr-4 pb-4 space-y-1">
            {/* Left accent rail */}
            <span
              className="absolute left-3 top-0 bottom-2 w-[3px] rounded-full bg-[#E5614C]/20"
              aria-hidden="true"
            />
            {link.children.map((c) => {
              const href = c.isHash && isHome ? c.href.replace('/', '') || '#' : c.href;
              const label = t(c.key);
              const childCls = 'block px-3 py-2.5 rounded-xl text-[15px] font-medium text-[#2A1B1A]/80 hover:bg-white hover:text-[#E5614C] transition-colors';
              if (c.isHash) {
                return (
                  <a key={c.key} href={href} className={childCls} onClick={closeMobile} data-testid={`mobile-nav-child-${c.key}`}>
                    {label}
                  </a>
                );
              }
              return (
                <Link key={c.key} to={c.href} className={childCls} onClick={closeMobile} data-testid={`mobile-nav-child-${c.key}`}>
                  {label}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Renders a single top-level nav link — plain link, hash link, or (when
 * `link.children` is present) a HoverDropdown on desktop / inline group on mobile.
 * Extracted from Header to avoid re-creating the component on every render.
 */
function NavItem({ link, mobile, t, isHome, closeMobile }) {
  // On desktop we keep the old inline styling; on mobile every nav item now
  // renders inside a rounded card that visually matches the About group card,
  // just without the chevron (since plain items don't expand).
  const desktopCls = 'relative text-[13px] xl:text-[15px] font-medium text-[#2A1B1A]/85 hover:text-[#E5614C] transition-colors duration-200 py-1 group whitespace-nowrap';
  const mobileCls = 'block w-full rounded-2xl bg-[#FAF3EE] border border-[#2A1B1A]/8 px-5 py-4 text-[#2A1B1A] font-semibold text-[17px] hover:text-[#E5614C] hover:border-[#E5614C]/25 transition-colors';
  const cls = mobile ? mobileCls : desktopCls;

  // Dropdown menu (desktop only; mobile shows children inline below the parent).
  if (link.children && !mobile) {
    return (
      <HoverDropdown
        triggerCls={cls}
        triggerLabel={t(link.key)}
        testId={`nav-dropdown-${link.key}`}
      >
        {link.children.map((c) => {
          const href = c.isHash && isHome ? c.href.replace('/', '') || '#' : c.href;
          const inner = <span className="block w-full">{t(c.key)}</span>;
          const itemCls = 'block px-3 py-2 rounded-md text-[14px] font-medium text-[#2A1B1A] hover:bg-[#FAF3EE] hover:text-[#E5614C] focus:bg-[#FAF3EE] focus:text-[#E5614C] focus:outline-none transition-colors';
          return c.isHash ? (
            <a key={c.key} href={href} className={itemCls} data-testid={`nav-dropdown-item-${c.key}`}>{inner}</a>
          ) : (
            <Link key={c.key} to={c.href} className={itemCls} data-testid={`nav-dropdown-item-${c.key}`}>{inner}</Link>
          );
        })}
      </HoverDropdown>
    );
  }

  // Mobile group: parent row that expands/collapses its children.
  // Auto-opens if the current route matches one of the children so the user
  // sees where they are without hunting.
  if (link.children && mobile) {
    return <MobileNavGroup link={link} t={t} isHome={isHome} closeMobile={closeMobile} />;
  }

  const inner = (
    <>
      {t(link.key)}
      {!mobile && (
        <span className="absolute -bottom-0.5 left-0 w-0 h-[2px] bg-[#E5614C] group-hover:w-full transition-[width] duration-300" />
      )}
    </>
  );

  if (link.isHash) {
    const href = isHome ? link.href.replace('/', '') || '#' : link.href;
    return (
      <a
        href={href}
        className={cls}
        onClick={mobile ? closeMobile : undefined}
        data-testid={mobile ? `mobile-nav-link-${link.key}` : undefined}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link
      to={link.href}
      className={cls}
      onClick={mobile ? closeMobile : undefined}
      data-testid={mobile ? `mobile-nav-link-${link.key}` : undefined}
    >
      {inner}
    </Link>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useI18n();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Force solid bg on non-home pages
  const solid = scrolled || !isHome;

  const closeMobile = () => setOpen(false);

  const current = languages.find((l) => l.code === lang) || languages[0];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        solid
          ? 'bg-[#FAF3EE] shadow-[0_4px_30px_rgba(42,27,26,0.06)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 xl:px-10 h-[78px] flex items-center justify-between gap-3 xl:gap-4">
        <Link to="/" className="flex items-center group shrink-0">
          <img
            src={LOGO_URL}
            alt="GEMS @ The Arts"
            className="h-14 md:h-16 w-auto group-hover:scale-105 transition-transform duration-300"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-7 whitespace-nowrap" aria-label="Primary">
          {navLinks.map((l) => (
            <NavItem key={l.key} link={l} t={t} isHome={isHome} closeMobile={closeMobile} />
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-1.5 xl:gap-2 shrink-0">
          {/* Language dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1.5 h-9 px-2.5 xl:px-3 rounded-full bg-white/80 border border-[#2A1B1A]/10 text-[#2A1B1A] text-[13px] font-semibold hover:bg-white hover:border-[#E5614C]/30 transition-colors"
                aria-label="Select language"
              >
                <Globe className="h-3.5 w-3.5" />
                {current.short}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className="cursor-pointer flex items-center justify-between"
                >
                  <span>{l.label}</span>
                  {lang === l.code && <Check className="h-4 w-4 text-[#E5614C]" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            asChild
            variant="ghost"
            className="rounded-full text-[#E5614C] hover:bg-[#E5614C]/10 hover:text-[#E5614C] font-semibold px-3 xl:px-4 whitespace-nowrap"
          >
            <Link to="/donate">{t('nav.donate')}</Link>
          </Button>
          <Button
            asChild
            className="rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold px-4 xl:px-5 shadow-[0_8px_20px_-6px_rgba(229,97,76,0.6)] btn-press whitespace-nowrap"
          >
            <Link to="/get-involved">{t('nav.getStarted')}</Link>
          </Button>
        </div>

        <button
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="mobile-menu"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden h-10 w-10 rounded-full bg-white shadow-md flex items-center justify-center text-[#2A1B1A]"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        id="mobile-menu"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${
          open ? 'max-h-[640px] opacity-100' : 'max-h-0 opacity-0'
        } bg-[#FAF3EE] border-t border-[#2A1B1A]/5`}
        aria-hidden={!open}
      >
        <div className="px-6 py-6 flex flex-col gap-3">
          {navLinks.map((l) => (
            <NavItem key={l.key} link={l} mobile t={t} isHome={isHome} closeMobile={closeMobile} />
          ))}

          <div className="flex flex-wrap gap-2 pt-3 border-t border-[#2A1B1A]/8" role="group" aria-label="Select language">
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                aria-pressed={lang === l.code}
                aria-label={`Switch language to ${l.label}`}
                className={`px-3 h-8 rounded-full text-[12px] font-semibold transition-colors ${
                  lang === l.code
                    ? 'bg-[#E5614C] text-white'
                    : 'bg-white text-[#2A1B1A] border border-[#2A1B1A]/10'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="flex gap-3 pt-2">
            <Button asChild variant="outline" className="flex-1 rounded-full border-[#E5614C] text-[#E5614C]">
              <Link to="/donate" onClick={() => setOpen(false)}>{t('nav.donate')}</Link>
            </Button>
            <Button asChild className="flex-1 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white">
              <Link to="/get-involved" onClick={() => setOpen(false)}>{t('nav.getStarted')}</Link>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
